import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Nestable from "react-nestable";
import { Button } from "antd";
import get from "lodash/get";
import { Save } from "lucide-react";

import { AddButton, ConfirmModal, Modal, Spinner, Tag } from "components";
import useAlert from "hooks/useAlert";
import useMainOrganization from "hooks/useMainOrganization";
import { helpers } from "services";
import ModulesActions from "modules/entity/actions";
import Create from "./components/Create";
import Update from "./components/Update";
import useTranslation from "../../hooks/useTranslation";
import { Fields } from "../../uicomponents";
import EmptyState from "assets/images/icons/state-empty.svg";
import { ReactComponent as EditIcon } from "assets/images/icons/pen.svg?react";
import { ReactComponent as DeleteIcon } from "assets/images/icons/trash.svg?react";

const Index = ({ asImport = false }) => {
	const { tr } = useTranslation();
	const navigate = useNavigate();
	const { type } = useParams();
	const { showAlert } = useAlert();
	const dispatch = useDispatch();

	const [createModal, showCreateModal] = useState(false);
	const [updateModal, showUpdateModal] = useState(false);
	const [deleteModal, showDeleteModal] = useState(false);
	const mainOrganization = useMainOrganization();
	const mainOrganizationId = get(mainOrganization, "id");
	const [selected, setSelected] = useState(null);
	const [items, setItems] = useState([]);
	const [isFetched, setIsFetched] = useState(false);
	const [isSubmitting, setSubmitting] = useState(false);

	const isFilial = useSelector(state => state.auth.is_filial_organization);

	const [selectedProducts, setSelectedProducts] = useState([]);

	const handleProductToggle = productId => {
		setSelectedProducts(prev =>
			prev.includes(productId)
				? prev.filter(id => id !== productId)
				: [...prev, productId]
		);
	};

	const allIds = items.map(item => item.id);

	const toggleAllProducts = () => {
		const isAllSelected = allIds.every(id => selectedProducts.includes(id));
		setSelectedProducts(isAllSelected ? [] : allIds);
	};

	const handleSave = () => {
		dispatch(
			ModulesActions.FormDefault.request({
				method: "post",
				url: "/main/sync",
				values: {
					alias: "categories",
					ids: selectedProducts
				},
				cb: {
					success: data => {
						showAlert(tr("Продукты успешно синхронизированы") + "!", {
							type: "success",
							delay: 5000
						});
					},
					error: error => {
						const errorMessage = get(error, "errorMessage");
						showAlert(errorMessage, { type: "danger", delay: 5000 });
					},
					finally: () => { }
				}
			})
		);
	};

	const openDeleteModal = item => {
		setSelected(item);
		showDeleteModal(true);
	};
	const openUpdateModal = item => {
		setSelected(item);
		showUpdateModal(true);
	};
	const closeDeleteModal = () => {
		showDeleteModal(false);
		setTimeout(() => {
			setSelected(null);
		}, 500);
	};
	const closeUpdateModal = () => {
		showUpdateModal(false);
		setTimeout(() => {
			setSelected(null);
		}, 500);
	};
	const deleteAction = () => {
		const id = get(selected, "id");
		setSubmitting(true);
		dispatch(
			ModulesActions.FormDefault.request({
				method: "delete",
				url: `/categories/${id}`,
				cb: {
					success: () => {
						showAlert(tr("Успешно удалено"), { type: "success" });
						loadItems();
						closeDeleteModal();
					},
					error: () => {
						showAlert("", { type: "danger" });
					},
					finally: () => {
						setSubmitting(false);
					}
				}
			})
		);
	};
	const updateMenuItems = items => {
		dispatch(
			ModulesActions.FormDefault.request({
				method: "put",
				url: `/categories/sort`,
				values: {
					nestable: items
				},
				cb: {
					success: () => {
						showAlert(tr("Успешно изменено"), { type: "success" });
						loadItems();
					},
					error: () => {
						showAlert(tr("Ошибка"), { type: "danger" });
					},
					finally: () => { }
				}
			})
		);
	};
	const loadItems = () => {
		setIsFetched(false);
		dispatch(
			ModulesActions.LoadDefault.request({
				url: "/categories",
				params: {
					filter: {
						type: type
					},
					extra: {
						root: 1,
						...(asImport ? { org_id: mainOrganizationId } : {})
					},
					sort: "sort",
					limit: 100,
					include: "children,file"
				},
				cb: {
					success: data => {
						setIsFetched(true);
						setItems(data.data);
					},
					error: () => {
						setIsFetched(true);
					}
				}
			})
		);
	};

	useEffect(() => {
		loadItems();
	}, [type]);

	return (
		<div>
			<Modal
				size={600}
				isOpen={createModal}
				toggle={() => showCreateModal(false)}
			>
				<Create {...{ showCreateModal, loadItems, type }} />
			</Modal>
			<Modal size={600} isOpen={updateModal} toggle={() => closeUpdateModal()}>
				<Update {...{ closeUpdateModal, loadItems, selected }} />
			</Modal>
			<Modal
				size={400}
				isOpen={deleteModal}
				toggle={() => showDeleteModal(false)}
				closeIcon={false}
			>
				<ConfirmModal
					text={tr(`Вы точно хотите удалить`) + "?"}
					onConfirmHandler={deleteAction}
					onCancelHandler={() => showDeleteModal(false)}
					isSubmitting={isSubmitting}
				/>
			</Modal>

			<div className="d-panel pos-relative">
				<div className="d-flex align-items-center justify-content-between mb-10">
					<div className="main-title w-full flex  justify-start items-center gap-5">
						{asImport && (
							<Fields.CheckboxD1
								position="right"
								handleChange={toggleAllProducts}
								checked={
									allIds.length > 0 &&
									allIds.every(id => selectedProducts.includes(id))
								}
								indeterminate={
									selectedProducts.length > 0 &&
									selectedProducts.length < allIds.length
								}
								className="mr-0 my-0 style-checkbox"
							/>
						)}
						{tr(helpers.getCategoryLabelByType(Number(type), tr))}
					</div>

					{asImport ? null : (
						<>
							{!isFilial ? (
								<AddButton
									title={tr("Добавить")}
									handleClick={() => showCreateModal(true)}
								/>
							) : (
								<>
									<AddButton
										hasPlus={false}
										title={tr("Импортировать")}
										handleClick={() =>
											navigate(`/categories/${type}/import`)
										}
									/>
								</>
							)}
						</>
					)}
				</div>

				{!isFetched && (
					<Spinner position={items.length > 0 ? "absolute" : "center"} />
				)}
				<Nestable
					maxDepth={2}
					items={items}
					childrenProp={"children"}
					collapsed={false}
					renderItem={({ item, collapseIcon }) => (
						<div className={`nestable-item__content`}>
							{asImport ? (
								<div className="flex items-center gap-2 fs-14 fw-600">
									<Fields.CheckboxS1
										className={"mb-0 mt-0 style-checkbox"}
										field={{
											value: selectedProducts.includes(item.id),
											onChange: e => {
												handleProductToggle(item.id);
											}
										}}
									//
									/>

									<p className="!my-0">{get(item, `name`)}</p>
								</div>
							) : (
								<div className="fs-14 fw-600">
									{collapseIcon} {get(item, `name`)}
								</div>
							)}
							<div className="d-flex align-items-center">
								<Tag color={helpers.getStatus(item.status, tr).color}>
									{tr(helpers.getStatus(item.status, tr).label)}
								</Tag>

								{asImport ? null : (
									<>
										<span
											className="ml-10 action-btn action-btn--info"
											onClick={() => openUpdateModal(item)}
										>
											<EditIcon />
										</span>
										<span
											className="action-btn action-btn--danger"
											onClick={() => openDeleteModal(item)}
										>
											<DeleteIcon />
										</span>
									</>
								)}
							</div>
						</div>
					)}
					onChange={items => {
						if (asImport) return null;

						updateMenuItems(items);
					}}
				/>

				{items.length < 1 && isFetched && (
					<div className="devent-table__no-data">
						<img src={EmptyState} alt="" />
						{tr("Список пуст")}
					</div>
				)}

				{asImport ? (
					<>
						<div className="h-[10px]" />

						<div className="flex justify-end items-center">
							<Button
								icon={<Save className="h-5 w-5 mr-2" />}
								onClick={handleSave}
								type="primary"
								className="w-60 mx-auto !rounded-lg flex items-center justify-center gap-2"
								size="large"
							>
								{tr("Сохранить меню")}
							</Button>
						</div>
					</>
				) : null}
			</div>
		</div>
	);
};

export default Index;
